import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useI18n } from '@/i18n/LanguageContext';
import { ShieldCheck, RefreshCw, AlertTriangle, Check, Copy, Gauge } from 'lucide-react';
import {
  runDeliverabilityAudit,
  DeliverabilityAudit,
  scoreTone,
  statusTone,
  severityTone,
} from '@/lib/email';

/**
 * ADMIN → EMAIL DELIVERY → AUTHENTICATION AUDIT
 * ---------------------------------------------------------------------------
 * Runs a LIVE audit (real DNS-over-HTTPS lookups, real send history) through
 * the admin-gated `email-deliverability-audit` edge function and renders:
 * SPF / DKIM / DMARC / envelope alignment / MX status, domain + IP reputation,
 * per-provider inbox-placement scores (Gmail, Outlook, Yahoo, Apple), the
 * ranked deliverability risks, the recommendations and the EXACT DNS records
 * still required. Nothing here is hardcoded — every value comes from the live
 * check, and each run is stored in mkt_email_audits for history.
 */

const Pill: React.FC<{ status?: string; label?: string }> = ({ status, label }) => (
  <span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ring-1 ${statusTone(status)}`}>
    {label || status || '—'}
  </span>
);

const AdminEmailAudit: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [audit, setAudit] = useState<DeliverabilityAudit | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState('');

  const load = async (persist = true) => {
    setBusy(true);
    setErr('');
    const res = await runDeliverabilityAudit(persist);
    if ((res as any).error) setErr((res as any).error);
    else setAudit(res as DeliverabilityAudit);
    setBusy(false);
  };

  useEffect(() => {
    // Show the most recent stored run instantly, then refresh live.
    supabase
      .from('mkt_email_audits')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data }) => {
        const r = data?.[0];
        if (r && !audit)
          setAudit({
            success: true,
            domain: r.domain,
            sendingDomain: r.bounce_domain?.envelopeDomain || '',
            checkedAt: r.created_at,
            overallScore: r.overall_score,
            providerScores: r.provider_scores,
            spf: r.spf,
            dkim: r.dkim,
            dmarc: r.dmarc,
            alignment: r.bounce_domain,
            mx: r.mx,
            reputation: r.reputation,
            risks: r.risks || [],
            recommendations: r.recommendations || [],
            dnsChanges: r.dns_changes || [],
            senderDomainAuth: { domain: '', verified: false, dkimSelectors: [], spf: [], dmarc: [], note: '' },
          } as DeliverabilityAudit);
        load();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (v: string) => {
    navigator.clipboard?.writeText(v);
    setCopied(v);
    setTimeout(() => setCopied(''), 1600);
  };

  const providers: { key: 'gmail' | 'outlook' | 'yahoo' | 'apple'; label: string }[] = [
    { key: 'gmail', label: 'Gmail' },
    { key: 'outlook', label: 'Outlook / Microsoft 365' },
    { key: 'yahoo', label: 'Yahoo / AOL' },
    { key: 'apple', label: 'Apple Mail / iCloud' },
  ];

  const checks = audit
    ? [
        { id: 'SPF', c: audit.spf, extra: audit.spf.record as string },
        { id: 'DKIM', c: audit.dkim, extra: (audit.dkim.selectors || []).join(', ') },
        { id: 'DMARC', c: audit.dmarc, extra: audit.dmarc.record as string },
        {
          id: es ? 'Alineación Return-Path' : 'Return-Path alignment',
          c: audit.alignment,
          extra: `From @${audit.alignment.fromDomain} · DKIM d=${audit.alignment.dkimSigningDomain} · envelope @${audit.alignment.envelopeDomain}`,
        },
        { id: es ? 'MX (recibir respuestas)' : 'MX (receive replies)', c: audit.mx, extra: (audit.mx.hosts || []).join(', ') },
      ]
    : [];

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
            <ShieldCheck className="h-5 w-5 text-orange-500" />
            {es ? 'Auditoría de autenticación y entrega' : 'Authentication & deliverability audit'}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            {es
              ? 'Consulta en vivo los registros DNS reales de giggone.com (SPF, DKIM, DMARC, MX, dominio de rebote) y el historial de envíos propio. No hay valores fijos en esta pantalla.'
              : 'Live lookup of the real giggone.com DNS records (SPF, DKIM, DMARC, MX, bounce domain) plus our own send history. No value on this screen is hardcoded.'}
          </p>
        </div>
        <button
          onClick={() => load()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          {busy ? (es ? 'Comprobando…' : 'Checking…') : es ? 'Ejecutar auditoría' : 'Run audit'}
        </button>
      </div>

      {err && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 ring-1 ring-red-200">{err}</p>
      )}

      {!audit ? (
        <p className="mt-6 text-sm text-slate-500">{es ? 'Cargando…' : 'Loading…'}</p>
      ) : (
        <>
          {/* ── scores ─────────────────────────────────────────────── */}
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">
                <Gauge className="h-3.5 w-3.5" /> {es ? 'Puntaje global' : 'Overall score'}
              </p>
              <p className={`mt-1 text-3xl font-extrabold ${scoreTone(audit.overallScore)}`}>{audit.overallScore}</p>
              <p className="text-xs text-slate-400">/100</p>
            </div>
            {providers.map((p) => (
              <div key={p.key} className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{p.label}</p>
                <p className={`mt-1 text-3xl font-extrabold ${scoreTone(audit.providerScores?.[p.key] ?? 0)}`}>
                  {audit.providerScores?.[p.key] ?? '—'}
                </p>
                <p className="text-xs text-slate-400">
                  {(audit.providerScores?.[p.key] ?? 0) >= 85
                    ? es ? 'Bandeja principal probable' : 'Primary inbox likely'
                    : (audit.providerScores?.[p.key] ?? 0) >= 65
                      ? es ? 'Riesgo de Promociones' : 'Promotions risk'
                      : es ? 'Riesgo de Spam' : 'Spam risk'}
                </p>
              </div>
            ))}
          </div>

          {/* ── authentication checks ──────────────────────────────── */}
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{es ? 'Comprobación' : 'Check'}</th>
                  <th className="px-4 py-3">{es ? 'Estado' : 'Status'}</th>
                  <th className="px-4 py-3">{es ? 'Detalle en vivo' : 'Live detail'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {checks.map((row) => (
                  <tr key={row.id} className="align-top">
                    <td className="px-4 py-3 font-bold text-slate-800">{row.id}</td>
                    <td className="px-4 py-3">
                      <Pill status={row.c?.status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      <p>{row.c?.note}</p>
                      {row.extra && (
                        <code className="mt-1 block break-all rounded bg-slate-50 px-2 py-1 text-xs text-slate-500">{row.extra}</code>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── reputation ─────────────────────────────────────────── */}
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">
                  {es ? 'Reputación del dominio' : 'Domain reputation'}
                </h3>
                <Pill status={audit.reputation?.status} />
              </div>
              <p className="mt-2 text-sm text-slate-600">{audit.reputation?.note}</p>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {[
                  [es ? 'Enviados (30 d)' : 'Sent (30 d)', audit.reputation?.sent],
                  [es ? 'Tasa de rebote' : 'Bounce rate', `${audit.reputation?.bounceRate ?? 0}% (≤${audit.reputation?.thresholds?.bounceRate ?? 2}%)`],
                  [es ? 'Tasa de queja' : 'Complaint rate', `${audit.reputation?.complaintRate ?? 0}% (≤${audit.reputation?.thresholds?.complaintRate ?? 0.1}%)`],
                  [es ? 'Direcciones suprimidas' : 'Suppressed addresses', audit.reputation?.suppressedAddresses],
                  [es ? 'Versión de texto' : 'Plain-text coverage', `${audit.reputation?.plainTextCoverage ?? 100}%`],
                  [es ? 'Manejo de rebotes' : 'Bounce handling', audit.reputation?.bounceHandling === 'active' ? (es ? 'Activo' : 'Active') : '—'],
                ].map(([k, v]) => (
                  <div key={String(k)} className="rounded-lg bg-slate-50 px-3 py-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</dt>
                    <dd className="font-bold text-slate-800">{String(v ?? '—')}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 text-xs text-slate-500">
                <strong>{es ? 'Reputación de IP: ' : 'IP reputation: '}</strong>
                {audit.reputation?.ipModel}
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-900">
                {es ? 'Riesgos de entrega' : 'Deliverability risks'}
              </h3>
              {audit.risks?.length === 0 ? (
                <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                  <Check className="h-4 w-4" /> {es ? 'Sin riesgos abiertos.' : 'No open risks.'}
                </p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {audit.risks.map((r, i) => (
                    <li key={i} className="rounded-lg border border-slate-100 p-3">
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ring-1 ${severityTone(r.severity)}`}>
                          {r.severity}
                        </span>
                        <p className="text-sm font-bold text-slate-800">{r.title}</p>
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-slate-600">{r.detail}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* ── DNS changes required ───────────────────────────────── */}
          <h3 className="mt-8 flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-slate-900">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {es ? 'Cambios de DNS requeridos' : 'DNS changes required'}
          </h3>
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-3">{es ? 'Prioridad' : 'Priority'}</th>
                  <th className="px-3 py-3">{es ? 'Tipo' : 'Type'}</th>
                  <th className="px-3 py-3">{es ? 'Host' : 'Host'}</th>
                  <th className="px-3 py-3">{es ? 'Valor' : 'Value'}</th>
                  <th className="px-3 py-3">{es ? 'Estado' : 'State'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {audit.dnsChanges?.map((d, i) => (
                  <tr key={i} className="align-top">
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ring-1 ${
                          d.priority === 'required'
                            ? 'bg-red-50 text-red-700 ring-red-200'
                            : d.priority === 'recommended'
                              ? 'bg-amber-50 text-amber-700 ring-amber-200'
                              : 'bg-slate-50 text-slate-600 ring-slate-200'
                        }`}
                      >
                        {d.priority}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-800">{d.type}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-700">{d.host}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-start gap-2">
                        <code className="block max-w-[340px] break-all rounded bg-slate-50 px-2 py-1 text-xs text-slate-700">{d.value}</code>
                        <button
                          onClick={() => copy(d.value)}
                          title={es ? 'Copiar' : 'Copy'}
                          className="rounded border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50"
                        >
                          {copied === d.value ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      <p className="mt-1 max-w-[420px] text-xs text-slate-500">{d.why}</p>
                    </td>
                    <td className="px-3 py-3">
                      <Pill status={d.done ? 'pass' : 'fail'} label={d.done ? (es ? 'Publicado' : 'Published') : es ? 'Falta' : 'Missing'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── recommendations ────────────────────────────────────── */}
          <h3 className="mt-8 text-sm font-extrabold uppercase tracking-wide text-slate-900">
            {es ? 'Recomendaciones de colocación en bandeja' : 'Inbox placement recommendations'}
          </h3>
          <ol className="mt-3 space-y-2">
            {audit.recommendations?.map((r, i) => (
              <li key={i} className="flex gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <span className="font-extrabold text-orange-500">{i + 1}.</span>
                <span>{r}</span>
              </li>
            ))}
          </ol>

          {audit.senderDomainAuth?.note && (
            <p className="mt-6 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <strong>{es ? 'Dominio de envío verificado: ' : 'Verified sending domain: '}</strong>
              {audit.senderDomainAuth.note}
            </p>
          )}
          <p className="mt-3 text-xs text-slate-400">
            {es ? 'Última comprobación: ' : 'Last checked: '}
            {new Date(audit.checkedAt).toLocaleString()}
          </p>
        </>
      )}
    </section>
  );
};

export default AdminEmailAudit;
